import { useFlightFiltersContext } from "@/app/lib/context/FlightFiltersContext";
import { useFlightSearchDataContext } from "@/app/lib/context/FlightSearchDataContext";
import {
  useFlightSelectionPayloadContext,
  type FlightType,
  type MappedFlightOffer,
} from "@/app/lib/context/FlightSelectionPayloadContext/FlightSelectionPayloadContext";
import type { FlightSearchQuery } from "@/app/lib/hooks/flightSearch/types";
import { useDepartureFlightSearch } from "@/app/lib/hooks/flightSearch/useDepartureFlightSearch";
import {
  useFlightQuickSearchTokens,
  useFlightSearchTokens,
} from "@/app/lib/hooks/useFlightSearchTokens";
import { FlightTrackingEvents, rudderAnalytics } from "@/app/lib/rudderstack";
import { TripType } from "@/app/lib/types/enums";
import type { FlightSearchType } from "@/app/lib/types/types";
import { FlightsList } from "@/app/ui/flight-list/FlightsList";
import { WhatsAppButton } from "@/app/ui/WhatsAppButton";
import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useTranslations } from "use-intl";

export const Route = createFileRoute(
  "/_authenticated/_flights/flights/departure/list",
)({
  component: RouteComponent,
});

function RouteComponent() {
  const navigate = Route.useNavigate();
  const t = useTranslations();

  const { searchData } = useFlightSearchDataContext();
  const { handleDepartureFlightSelection, selectedDepartureFlightId } =
    useFlightSelectionPayloadContext();
  const { query: tokensQuery, cancel: cancelTokensQuery } =
    useFlightSearchTokens();
  const quickTokensQuery = useFlightQuickSearchTokens();
  const { filters } = useFlightFiltersContext();
  const fullSearchQuery = useDepartureFlightSearch(
    tokensQuery,
    filters,
    "full",
  );
  const quickSearchQuery = useDepartureFlightSearch(
    quickTokensQuery,
    null,
    "quick",
  ); // quick search does not use any filters

  const [fullSearchLoaded, setFullSearchLoaded] = useState(
    Boolean(selectedDepartureFlightId),
  );

  const handleFlightSelect = (
    offer: MappedFlightOffer,
    flight: FlightType,
    queryType: FlightSearchType | null,
  ) => {
    rudderAnalytics.track(
      FlightTrackingEvents.USER_SELECTED_PARTICULAR_FLIGHT,
      {
        offer_id: offer.index,
        offer_type: "Flight",
        price: offer.totalPrice,
      },
    );

    handleDepartureFlightSelection(offer, flight.key, queryType);
    if (queryType === "quick") {
      cancelTokensQuery();
    }
    if (searchData?.tripType === TripType.ROUND_TRIP) {
      navigate({
        to: "/flights/return/list",
      });
    } else {
      navigate({
        to: "/flights/offer/$id/step/1",
        params: { id: offer.index },
      });
    }
  };

  // we need this to know if full search succeeded in the first place in order to show
  // full search results even if it's refetching (for example becaue of filters change)
  useEffect(() => {
    if (fullSearchQuery.isSuccess) {
      setFullSearchLoaded(true);
    }
  }, [fullSearchQuery.isSuccess]);

  // we need to reset this flag when search data changes
  useEffect(() => {
    const handleSearchDataUpdated: WindowEventHandler<
      "searchdataupdated"
    > = () => {
      setFullSearchLoaded(false);
    };

    window.addEventListener("searchdataupdated", handleSearchDataUpdated);

    return () => {
      window.removeEventListener("searchdataupdated", handleSearchDataUpdated);
    };
  }, []);

  const searchQuery: {
    query: FlightSearchQuery;
    type: FlightSearchType | null;
  } =
    quickSearchQuery.isEmpty || fullSearchLoaded
      ? { query: fullSearchQuery, type: "full" }
      : { query: quickSearchQuery, type: "quick" };

  return (
    <main className="no-footer main h-screen">
      <FlightsList
        onFlightSelect={handleFlightSelect}
        searchQuery={searchQuery}
        direction="departure"
        title={
          searchData?.tripType === TripType.ROUND_TRIP ? (
            <div>{t("flights.chooseDepartureFlight")}</div>
          ) : null
        }
        isLoadingAdditionalData={
          fullSearchQuery.isFetching || quickSearchQuery.isFetching
        }
      />
      <WhatsAppButton />
    </main>
  );
}
